"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import NyhetsPopupVy, { type NyhetVisning } from "./NyhetsPopupVy";

// Paths where the popup must never appear: login (no session), the paired
// mobile view, the print view and the local design demo (renders the view
// itself). Prefix match — same guard style as Nav.
const TYSTA_PREFIX = ["/login", "/m", "/utskrift", "/nyheter-test", "/byt-losenord"];

// Layout-level one-shot news popup. Fetches the user's unseen news once per
// full page load; shows nothing when the list is empty or any request fails
// (the popup is a bonus — it must never break a page). Dismissing marks every
// fetched item as seen server-side, so it appears exactly once per user.
export default function NyhetsPopup() {
  const pathname = usePathname();
  const [nyheter, setNyheter] = useState<NyhetVisning[] | null>(null);

  const tyst = TYSTA_PREFIX.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  useEffect(() => {
    if (tyst) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/nyheter/olasta", { cache: "no-store" });
        if (!res.ok) return;
        const data = (await res.json()) as { nyheter?: NyhetVisning[] };
        if (!cancelled && Array.isArray(data.nyheter) && data.nyheter.length > 0) {
          setNyheter(data.nyheter);
        }
      } catch {
        // silent — no popup is always a safe outcome
      }
    })();
    return () => {
      cancelled = true;
    };
    // Fetch once per mount (= once per full page load), not per navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stang = useCallback(() => {
    const ids = nyheter?.map((n) => n.id) ?? [];
    setNyheter(null);
    if (ids.length === 0) return;
    fetch("/api/nyheter/sedd", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    }).catch(() => {
      // silent — worst case the popup shows once more on next load
    });
  }, [nyheter]);

  if (tyst || !nyheter) return null;
  return <NyhetsPopupVy nyheter={nyheter} onStang={stang} />;
}
